// Tipos gerados a partir de API_CONTRACT.md

export enum UserRole {
  USER = "USER",
  ADMIN = "ADMIN",
}

export enum GiftStatus {
  DRAFT = "DRAFT",
  PENDING_PAYMENT = "PENDING_PAYMENT",
  PUBLISHED = "PUBLISHED",
}

export enum Plan {
  BASIC = "BASIC",
  PREMIUM = "PREMIUM",
  DELUXE = "DELUXE",
}

export enum PrivacyMode {
  PUBLIC = "PUBLIC",
  PASSWORD = "PASSWORD",
}

export enum Occasion {
  ANNIVERSARY = "ANNIVERSARY",
  BIRTHDAY = "BIRTHDAY",
  VALENTINES_DAY = "VALENTINES_DAY",
  MARRIAGE = "MARRIAGE",
  PROPOSAL = "PROPOSAL",
  OTHER = "OTHER",
}

export enum RecipientType {
  LOVE = "LOVE",
  FRIEND = "FRIEND",
  MOM = "MOM",
  DAD = "DAD",
  GRANDMA = "GRANDMA",
  SISTER = "SISTER",
  AUNT = "AUNT",
  DAUGHTER = "DAUGHTER",
  GODMOTHER = "GODMOTHER",
  MOTHER_IN_LAW = "MOTHER_IN_LAW",
  TEACHER = "TEACHER",
  BOSS = "BOSS",
}

export enum PaymentProvider {
  MERCADO_PAGO = "MERCADO_PAGO",
  STRIPE = "STRIPE",
}

export enum PaymentStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  EXPIRED = "EXPIRED",
  REFUNDED = "REFUNDED",
}

export enum PaymentMethod {
  PIX = "PIX",
  CREDIT_CARD = "CREDIT_CARD",
  BOLETO = "BOLETO",
}

export enum AiGenerationType {
  LOVE_LETTER = "LOVE_LETTER",
  IMPROVE_TEXT = "IMPROVE_TEXT",
  TITLE = "TITLE",
  PHRASE = "PHRASE",
  TIMELINE = "TIMELINE",
}

export enum AiTone {
  ROMANTICO = "ROMANTICO",
  POETICO = "POETICO",
  DIVERTIDO = "DIVERTIDO",
  INTENSO = "INTENSO",
  FORMAL = "FORMAL",
}

export type ISODateTime = string;
export type UUID = string;

export interface User {
  id: UUID;
  name: string;
  email: string;
  role: UserRole;
  phone?: string | null;
  createdAt: ISODateTime;
  updatedAt: ISODateTime;
  lastLoginAt?: ISODateTime | null;
}

export interface GiftPhoto {
  id: UUID;
  giftId: UUID;
  url: string;
  width: number;
  height: number;
  sizeBytes: number;
  order: number;
  createdAt: ISODateTime;
}

export interface GiftTimelineEvent {
  id: UUID;
  giftId: UUID;
  title: string;
  description?: string | null;
  eventDate: string;
  order: number;
  createdAt: ISODateTime;
  updatedAt: ISODateTime;
}

export interface GiftTemplate {
  id: UUID;
  slug: string;
  name: string;
  description?: string | null;
  thumbnailUrl: string;
  isPremium: boolean;
  planRequired: Plan;
  config: Record<string, unknown>;
  createdAt: ISODateTime;
  updatedAt: ISODateTime;
}

// Dados personalizados de cada experiência/template extra. Cada chave é o
// experienceId (ex: "timeline", "surprise-wheel") e o valor é um objeto
// específico daquela experiência. Mantemos o tipo aberto via Record para
// permitir evolução incremental sem quebrar contrato — cada seção valida
// o que precisa quando consome os próprios dados (com fallback elegante).
export type ExperienceDataMap = Record<string, Record<string, unknown>>;

export interface Gift {
  id: UUID;
  userId: UUID;
  title: string;
  slug: string;
  status: GiftStatus;
  plan: Plan;
  privacyMode: PrivacyMode;
  passwordEnabled: boolean;
  occasion: Occasion;
  recipientName?: string | null;
  partnerName?: string | null;
  loveLetter: string;
  musicUrl?: string | null;
  // Metadados opcionais da música (nome, artista, preview MP3, capa) usados
  // pelo layout estilo Spotify da página pública.
  musicName?: string | null;
  artistName?: string | null;
  musicPreviewUrl?: string | null;
  artworkUrl?: string | null;
  coupleStartDate: string;
  coverPhotoUrl?: string | null;
  templateSlug?: string | null;
  photos: GiftPhoto[];
  timelineEvents: GiftTimelineEvent[];
  // IDs das experiências/módulos extras selecionadas pelo usuário antes do
  // pagamento. Renderizadas dinamicamente na LovePage pública.
  selectedExperiences?: string[];
  // Dados personalizados de cada experiência. Opcional: gifts antigos
  // continuam funcionando com fallback nas seções publicadas.
  experienceData?: ExperienceDataMap;
  publishedAt?: ISODateTime | null;
  createdAt: ISODateTime;
  updatedAt: ISODateTime;
}

export interface Payment {
  id: UUID;
  giftId: UUID;
  userId: UUID;
  provider: PaymentProvider;
  status: PaymentStatus;
  method: PaymentMethod;
  currency: "BRL";
  amountCents: number;
  discountCents: number;
  finalAmountCents: number;
  couponCode?: string | null;
  providerReference?: string | null;
  checkoutUrl?: string | null;
  pixQrCodeBase64?: string | null;
  pixCopyPaste?: string | null;
  paidAt?: ISODateTime | null;
  expiresAt?: ISODateTime | null;
  createdAt: ISODateTime;
  updatedAt: ISODateTime;
}

export interface Coupon {
  id: UUID;
  code: string;
  description?: string | null;
  discountType: "PERCENT" | "FIXED";
  discountValue: number;
  maxRedemptions?: number | null;
  redemptions: number;
  isActive: boolean;
  validFrom: ISODateTime;
  validUntil?: ISODateTime | null;
  applicablePlans: Plan[];
  createdAt: ISODateTime;
  updatedAt: ISODateTime;
}

export interface GiftView {
  id: UUID;
  giftId: UUID;
  viewedAt: ISODateTime;
  referrer?: string | null;
  userAgentHash?: string | null;
  ipHash?: string | null;
  country?: string | null;
  city?: string | null;
}

export interface AiGeneration {
  id: UUID;
  userId: UUID;
  giftId?: UUID | null;
  type: AiGenerationType;
  tone: AiTone;
  input: Record<string, unknown>;
  output: Record<string, unknown>;
  tokensUsed?: number | null;
  createdAt: ISODateTime;
}

// Tipos de resposta da API
export interface ApiSuccess<T> {
  success: true;
  data: T;
}

export interface ApiError {
  success: false;
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
}

export type ApiResponse<T> = ApiSuccess<T> | ApiError;

// Payloads de request
export interface RegisterRequest {
  name: string;
  email: string;
  password: string;
  acceptTerms: boolean;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

export interface UploadedPhoto {
  url: string;
  storageKey?: string;
  width?: number;
  height?: number;
  sizeBytes?: number;
}

export interface CreateGiftRequest {
  title: string;
  slug: string;
  plan: Plan;
  occasion: Occasion;
  privacyMode: PrivacyMode;
  password?: string;
  recipientName?: string;
  partnerName?: string;
  loveLetter: string;
  coupleStartDate: string;
  musicUrl?: string;
  musicName?: string;
  artistName?: string;
  musicPreviewUrl?: string;
  artworkUrl?: string;
  templateSlug?: string;
  coverPhotoUrl?: string;
  /** URLs de fotos já enviadas via /uploads/images (pré-checkout).
   *  Backend cria GiftPhoto rows a partir dessa lista. */
  photoUrls?: string[];
  selectedExperiences?: string[];
  experienceData?: ExperienceDataMap;
}

export interface UpdateGiftRequest {
  title?: string;
  loveLetter?: string;
  coverPhotoUrl?: string;
  musicUrl?: string;
  musicName?: string;
  artistName?: string;
  musicPreviewUrl?: string;
  artworkUrl?: string;
  privacyMode?: PrivacyMode;
  password?: string;
  occasion?: Occasion;
  recipientName?: string;
  partnerName?: string;
  coupleStartDate?: string;
  templateSlug?: string;
  selectedExperiences?: string[];
  experienceData?: ExperienceDataMap;
}

export interface CheckoutRequest {
  giftId: UUID;
  provider: PaymentProvider;
  method: PaymentMethod;
  couponCode?: string;
}

export interface ValidateCouponRequest {
  code: string;
  plan: Plan;
}

export interface UnlockGiftRequest {
  password: string;
}

export interface AiLoveLetterRequest {
  giftId?: UUID;
  tone: AiTone;
  context: string;
  maxWords?: number;
}

export interface AiImproveTextRequest {
  giftId?: UUID;
  tone: AiTone;
  text: string;
  goal: string;
}

export interface AiGenerateExperienceRequest {
  giftId?: UUID;
  experienceId: string;
  tone?: AiTone;
  context?: string;
}

// Wizard state
export interface WizardState {
  step: number;
  recipientType?: RecipientType;
  coupleData: {
    recipientName: string;
    partnerName: string;
    coupleStartDate: string;
    occasion: Occasion;
    title: string;
    slug: string;
  };
  letter: {
    loveLetter: string;
  };
  /** Fotos já enviadas pro storage (Cloudinary/local) ANTES do checkout.
   *  Mudamos de File[] pra esse shape pra que sobreviva a reload — File
   *  não pode ser serializado pro localStorage. O upload acontece já no
   *  momento em que o usuário escolhe a foto no PhotoUploader. */
  photos: UploadedPhoto[];
  music: {
    musicUrl: string;
    musicName?: string;
    artistName?: string;
    musicPreviewUrl?: string;  // URL MP3 de preview (iTunes 30s)
    artworkUrl?: string;        // Capa do álbum
  };
  theme: {
    templateSlug: string;
    /** URL de uma imagem custom carregada pelo usuário pra ser usada como
     *  capa/background do tema (substitui o gradient padrão). Quando set,
     *  `templateSlug` vira "custom-photo" (opaca) ou
     *  "custom-photo-sharp" (nítida). */
    customBackgroundUrl?: string;
    /** true = blur + brilho reduzido (ambiente, deixa o texto da página
     *  legível). false = imagem nítida em cima ocupando o topo inteiro,
     *  estilo pôster. Default: true (opaca) — mais seguro pra leitura. */
    customBackgroundOpaque?: boolean;
  };
  privacy: {
    privacyMode: PrivacyMode;
    password: string;
  };
  plan: Plan;
  // IDs das experiências/templates diferenciados adicionados como módulos
  // extras opcionais à LovePage. Persistidos junto com os dados do gift.
  selectedExperiences: string[];
  // Dados personalizados de cada experiência selecionada. A chave é o
  // experienceId. Os dados ficam aqui até o checkout, quando são filtrados
  // apenas para as experiências realmente selecionadas.
  experienceData: ExperienceDataMap;
}
